import { Button, Divider, Drawer, InputNumber, Select, Space } from 'antd';
import React, { useState, useMemo, useEffect } from 'react';
import { useGetCategoriesList } from '../../../../../utils/api/categories/useGetCategoriesList';
import { useGetMediaTags } from '../../../../../utils/api/medias/useGetMediaTags';
import {
  MEDIA_DATE_PERIOD_OPTIONS,
  MEDIA_DIMENSIONS_OPTIONS,
  MEDIA_FILE_SIZE_OPTIONS,
  MEDIA_FORMATS_OPTIONS,
  MEDIA_RESOLUTION_OPTIONS,
  MEDIA_STATUS_OPTIONS,
  MEDIAS_TYPES_OPTIONS,
} from '../../../../../utils/const/medias';
import { useMediaToQualifyFilters } from '../../../../../utils/zustand/hooks/useMediaToQualifyFilters';

const FilterDrawer = ({ open, onClose }) => {
  const {
    filters,
    setSelectedStatusIdFilter,
    setSelectedTypeFilter,
    setSelectedTagsFilter,
    setSelectedMediaFormatFilter,
    setSelectedOrderFilter,
    setSelectedCategoryNameFilter,
    setSelectedDimensionsFilter,
    setSelectedSizesFilter,
    setSelectedResolutionsFilter,
    setSelectedCreatedAtFilter,
    clearFilters,
  } = useMediaToQualifyFilters();

  const [searchedTag, setSearchedTag] = useState('');
  const [debouncedSearchTag, setDebouncedSearchTag] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTag(searchedTag.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchedTag]);

  const { tags, isLoading: isTagLoading } = useGetMediaTags({
    search: debouncedSearchTag,
  });

  const { categories } = useGetCategoriesList();

  const tagOptions = useMemo(() => {
    return tags?.map((tag) => ({
      label: tag.name,
      value: tag.name,
    }));
  }, [tags]);

  const categorieOptions = useMemo(() => {
    return categories?.map((categorie) => ({
      label: categorie.name,
      value: categorie.name,
    }));
  }, [categories]);

  const normalizeTagsValue = (tags) => {
    if (tags === 'all' || tags === null || tags === undefined) return [];
    return Array.isArray(tags) ? tags : [];
  };

  const handleTagsChange = (value) => {
    setSearchedTag('');
    setSelectedTagsFilter(!value || value.length === 0 ? 'all' : value);
  };

  return (
    <Drawer
      title="Filtres avancés"
      placement="right"
      onClose={onClose}
      open={open}
      width={400}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Type</div>
          <Select
            placeholder="Type de média"
            style={{ width: '100%' }}
            options={MEDIAS_TYPES_OPTIONS}
            value={filters.selectedType}
            onChange={setSelectedTypeFilter}
            allowClear
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Tag</div>
          <Select
            mode="multiple"
            placeholder="Sélectionner des tags"
            style={{ width: '100%' }}
            options={tagOptions}
            value={normalizeTagsValue(filters.selectedTags)}
            onChange={handleTagsChange}
            searchValue={searchedTag}
            onSearch={setSearchedTag}
            loading={isTagLoading}
            allowClear
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Format</div>
          <Select
            placeholder="Format de fichier"
            style={{ width: '100%' }}
            options={MEDIA_FORMATS_OPTIONS}
            value={filters.selectedMediaFormat}
            onChange={setSelectedMediaFormatFilter}
            allowClear
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Ordre</div>
          <InputNumber
            placeholder="Entrer un numéro d'ordre entre 0 et 100"
            style={{ width: '100%' }}
            min={0}
            max={100}
            value={filters.selectedOrder}
            onChange={setSelectedOrderFilter}
            allowClear
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Dimensions</div>
          <Select
            placeholder="Dimensions"
            style={{ width: '100%' }}
            options={MEDIA_DIMENSIONS_OPTIONS}
            value={filters.selectedDimensions}
            onChange={setSelectedDimensionsFilter}
            allowClear
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Catégorie</div>
          <Select
            placeholder="Catégorie de produit"
            style={{ width: '100%' }}
            options={categorieOptions}
            value={filters.selectedCategoryName}
            onChange={setSelectedCategoryNameFilter}
            allowClear
            showSearch
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            Date d&apos;ajout
          </div>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              placeholder="Période"
              style={{ width: '100%' }}
              options={MEDIA_DATE_PERIOD_OPTIONS}
              value={filters.selectedCreatedAt}
              onChange={setSelectedCreatedAtFilter}
              allowClear
            />
          </Space>
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            Taille du fichier
          </div>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              placeholder="Taille"
              style={{ width: '100%' }}
              options={MEDIA_FILE_SIZE_OPTIONS}
              value={filters.selectedSizes}
              onChange={setSelectedSizesFilter}
              allowClear
            />
          </Space>
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Statut</div>
          <Select
            placeholder="Statut"
            style={{ width: '100%' }}
            options={MEDIA_STATUS_OPTIONS}
            value={filters.selectedStatusId}
            onChange={setSelectedStatusIdFilter}
            allowClear
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            Résolution (images)
          </div>
          <Select
            placeholder="Résolution"
            style={{ width: '100%' }}
            options={MEDIA_RESOLUTION_OPTIONS}
            value={filters.selectedResolutions}
            onChange={setSelectedResolutionsFilter}
            allowClear
          />
        </div>

        <Divider />

        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>Fermer</Button>
          <Button type="primary" danger onClick={() => clearFilters()}>
            Réinitialiser tout
          </Button>
        </Space>
      </Space>
    </Drawer>
  );
};

export default FilterDrawer;
